import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  ArrowRight,
  CheckCircle2,
  CloudLightning,
  Shield,
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

const areasSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": "https://www.saskroofingcompany.com/areas#webpage",
  url: "https://www.saskroofingcompany.com/areas",
  name: "Saskatchewan Roofing Service Areas | Sask Roofing",
  isPartOf: { "@id": "https://www.saskroofingcompany.com/#website" },
  about: { "@id": "https://www.saskroofingcompany.com/#localbusiness" },
  mainEntity: {
    "@type": "ItemList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        url: "https://www.saskroofingcompany.com/areas/saskatoon",
        name: "Saskatoon Roofing",
      },
      {
        "@type": "ListItem",
        position: 2,
        url: "https://www.saskroofingcompany.com/areas/regina",
        name: "Regina Roofing",
      },
      {
        "@type": "ListItem",
        position: 3,
        url: "https://www.saskroofingcompany.com/areas/moose-jaw",
        name: "Moose Jaw Roofing",
      },
      {
        "@type": "ListItem",
        position: 4,
        url: "https://www.saskroofingcompany.com/areas/prince-albert",
        name: "Prince Albert Roofing",
      },
      {
        "@type": "ListItem",
        position: 5,
        url: "https://www.saskroofingcompany.com/areas/swift-current",
        name: "Swift Current Roofing",
      },
      {
        "@type": "ListItem",
        position: 6,
        url: "https://www.saskroofingcompany.com/areas/yorkton",
        name: "Yorkton Roofing",
      },
      {
        "@type": "ListItem",
        position: 7,
        url: "https://www.saskroofingcompany.com/areas/estevan",
        name: "Estevan Roofing",
      },
      {
        "@type": "ListItem",
        position: 8,
        url: "https://www.saskroofingcompany.com/areas/weyburn",
        name: "Weyburn Roofing",
      },
    ],
  },
};

export default function Areas() {
  const areas = [
    {
      name: "Saskatoon",
      desc: "Expert roof replacement and repair services for Saskatoon homeowners. We've completed hundreds of projects across the Bridge City.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/78aa047c-2ebb-4e6e-a89c-b9e0f92fe325.png&output=webp&q=60",
      stats: "500+ Roofs Completed",
    },
    {
      name: "Regina",
      desc: "Trusted roofing contractor serving Regina and surrounding communities with climate-tested materials and local expertise.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a545f070-aa2a-4c1f-8dc3-842c3234e2ad.png&output=webp&q=60",
      stats: "400+ Roofs Completed",
    },
    {
      name: "Moose Jaw",
      desc: "Reliable roof repairs and installations in the Moose Jaw area. We understand the unique high-wind challenges of the region.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/01402efe-8f21-42b2-bc66-0286199a2300.png&output=webp&q=60",
      stats: "150+ Roofs Completed",
    },
    {
      name: "Prince Albert",
      desc: "Durable roofing solutions designed for Prince Albert's heavy snow loads and northern Saskatchewan weather patterns.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b963df3a-8576-4537-8ff4-9d7e9bcdc2c5.png&output=webp&q=60",
      stats: "120+ Roofs Completed",
    },
    {
      name: "Swift Current",
      desc: "Quality roofing services tailored for Swift Current homes. Our roofs are built to withstand the extreme prairie winds.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/58ccf1f5-72ed-44a9-9bc9-0564b2bc0915.png&output=webp&q=60",
      stats: "100+ Roofs Completed",
    },
    {
      name: "Yorkton",
      desc: "Professional roofers delivering top-tier results in Yorkton. We specialize in hail-resistant roofing for eastern Saskatchewan.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60",
      stats: "150+ Roofs Completed",
    },
    {
      name: "Estevan",
      desc: "Comprehensive roofing replacements and repairs for Estevan. We handle extreme temperature swings with ease.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/6e2ca68f-b65c-4e53-8f1f-3c188b15b51c.png&output=webp&q=60",
      stats: "80+ Roofs Completed",
    },
    {
      name: "Weyburn",
      desc: "Protecting Weyburn homes with premium roofing materials and expert installation from local Saskatchewan crews.",
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b4e160d4-9554-4875-9ea8-63080cc9fab9.png&output=webp&q=60",
      stats: "90+ Roofs Completed",
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Saskatchewan Roofing Service Areas | Sask Roofing"
        description="Serving 8 cities across Saskatchewan: Saskatoon, Regina, Moose Jaw, Prince Albert, Swift Current, Yorkton, Estevan, Weyburn. Free inspections."
        canonical="/areas"
        schemaJson={[areasSchema]}
      />
      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/d44209a5-86d7-4abe-907f-4530f2cb2035.png&output=webp&q=60"
            alt="Saskatchewan Roofing Service Areas"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Roofing Services Across Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              We proudly serve 8 major cities and dozens of surrounding
              communities across Saskatchewan. Our local crews understand the
              unique weather challenges of the prairies.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl mx-auto">
          <div className="text-center space-y-6">
            <h2 className="text-3xl font-bold">
              Local Experts, Province-Wide Service
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Sask Roofing Company was founded on the belief that Saskatchewan
              homeowners deserve a roofing contractor who truly understands our
              climate. We don't just serve one city; we have dedicated local
              crews stationed across the province to ensure rapid response times
              and expert installation in Saskatoon, Regina, Moose Jaw, Prince
              Albert, and beyond. Whether you're facing a northern snow load or
              a southern hailstorm, we have the specialized knowledge to protect
              your home.
            </p>
          </div>
        </div>
      </section>

      {/* Climate Section */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold">
                Why Saskatchewan Roofs Are Different
              </h2>
              <p className="text-lg text-muted-foreground">
                A roof that works in Ontario or BC will fail in Saskatchewan.
                Our province experiences some of the most extreme temperature
                swings on the planet, ranging from -40°C deep freezes to +35°C
                summer heat. These rapid changes cause massive expansion and
                contraction in roofing materials.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary font-bold">
                    <CloudLightning className="w-5 h-5" />
                    <span>The Hail Corridor</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    We utilize Class 3 and 4 impact-rated materials specifically
                    designed to survive the severe hailstorms common across the
                    SK plains.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary font-bold">
                    <Shield className="w-5 h-5" />
                    <span>Wind Resistance</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Our high-wind nailing patterns and reinforced starters are
                    engineered to withstand the relentless 100km/h+ gusts of the
                    prairies.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-background p-8 rounded-2xl border shadow-sm space-y-6">
              <h3 className="text-2xl font-bold">
                Common Regional Issues We Solve
              </h3>
              <ul className="space-y-4">
                {[
                  "Ice damming caused by poor attic ventilation and insulation balance.",
                  "Wind-lifted shingles on exposed rural and suburban properties.",
                  "Hail bruising that compromises shingle integrity without immediate leaks.",
                  "Premature shingle failure due to extreme UV exposure and heat.",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* City Grid */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4">Our Core Service Hubs</h2>
            <p className="text-lg text-muted-foreground">
              Click on your city to see detailed local roofing services,
              pricing, and permit information.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {areas.map((city) => (
              <Link
                key={city.name}
                to={`/areas/${city.name.toLowerCase().replace(" ", "-")}`}
                className="group block"
              >
                <div className="bg-muted/30 rounded-xl border overflow-hidden transition-all hover:shadow-lg hover:border-primary h-full flex flex-col">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={city.image}
                      alt={`${city.name} Roofing`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col space-y-4">
                    <div className="flex justify-between items-start">
                      <h3 className="text-xl font-bold group-hover:text-primary transition-colors">
                        {city.name}
                      </h3>
                      <MapPin className="w-5 h-5 text-primary opacity-50" />
                    </div>
                    <p className="text-muted-foreground text-sm flex-1">
                      {city.desc}
                    </p>
                    <div className="pt-2 border-t border-primary/10">
                      <span className="text-xs font-bold text-primary uppercase tracking-wider">
                        {city.stats}
                      </span>
                    </div>
                    <div className="text-primary font-bold flex items-center gap-2 pt-2 text-sm">
                      View Local Services <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-20 bg-muted/50 border-t">
        <div className="container px-4 text-center max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl font-bold">Don't See Your Community?</h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            While we focus on these 8 major hubs, we regularly serve smaller
            towns and rural properties within a 100km radius of each city. If
            you're in a surrounding Saskatchewan community and need a reliable
            roofer, chances are we have a crew in your area.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Check My Area</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto bg-white border-primary text-primary hover:bg-primary/5"
              asChild
            >
              <a href="tel:6397397969">(639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
